import { LitElement, html, unsafeCSS } from 'lit'
import { property } from 'lit/decorators.js'
import { unsafeSVG } from 'lit/directives/unsafe-svg.js'
import { PICTOGRAM_TIME_LIMIT_STATIC, PICTOGRAM_ERROR_STATIC } from '@getgo/chameleon-pictograms'
import { asTranslationKey, getTranslation } from '@/i18n/translate-utils'
import styles from './gallery-error.styles.scss?inline'

/** The reason category driving which empty-state content to display. */
export type GalleryErrorReason = 'expired' | 'not-found' | 'error'

/**
 * Error/empty-state component for the GoTo Gallery SPA.
 *
 * Renders a centred `<chameleon-empty-state>` with an icon, title, and body
 * appropriate to the given `reason`.
 *
 * - `expired`   → clock icon, link-expired messaging
 * - `not-found` → error icon, page-not-found messaging
 * - `error`     → error icon, generic error messaging
 *
 * @prop reason - The error category to display.
 */
export class GalleryError extends LitElement {
  static readonly tagName = 'gallery-error'
  static styles = unsafeCSS(styles)

  /** The error reason that controls which icon, title, and body are shown. */
  @property({ type: String }) reason: GalleryErrorReason = 'error'

  override render() {
    const isExpired = this.reason === 'expired'

    const icon = isExpired ? PICTOGRAM_TIME_LIMIT_STATIC : PICTOGRAM_ERROR_STATIC

    const titleKey = isExpired
      ? asTranslationKey('gallery.error.expired.title', 'Link expired')
      : this.reason === 'not-found'
        ? asTranslationKey('gallery.error.notFound.title', "This page doesn't exist")
        : asTranslationKey('gallery.error.generic.title', 'Something went wrong')

    const bodyKey = isExpired
      ? asTranslationKey('gallery.error.expired.body', 'Ask the sender for a new link to access these files.')
      : this.reason === 'not-found'
        ? asTranslationKey('gallery.error.notFound.body', 'If files were shared with you, ask for a new link.')
        : asTranslationKey('gallery.error.generic.body', "We couldn't load the gallery. Please try again later.")

    return html`
      <chameleon-empty-state size="medium">
        <chameleon-svg class="pictogram" slot="icon">${unsafeSVG(icon)}</chameleon-svg>
        <p slot="title">${getTranslation(titleKey)}</p>
        ${getTranslation(bodyKey)}
      </chameleon-empty-state>
    `
  }
}
